<template>
  <div>
    <p class="text-muted small mb-3">{{ $t("dette.initialReceiptHint") }}</p>
    <div class="mb-3">
      <div class="text-muted mb-1">{{ $t("dette.totalAmount") }}</div>
      <a-input-number :value="totalAmount" disabled style="width: 100%" :precision="3" />
    </div>

    <div v-for="(line, idx) in paymentLines" :key="line.uid" class="payment-split-card mb-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <strong>{{ $t("encaissementValidation.paymentLine", { n: idx + 1 }) }}</strong>
        <a-button
          v-if="paymentLines.length > 1"
          type="link"
          size="small"
          class="text-danger p-0"
          @click="removePaymentLine(idx)"
        >
          {{ $t("encaissementValidation.removePaymentLine") }}
        </a-button>
      </div>
      <a-row :gutter="[12, 8]">
        <a-col :xs="24" :sm="12">
          <div class="mb-2">
            <label class="filter-label">{{ $t("encaissementValidation.paymentMethod") }}</label>
            <a-select v-model="line.method" style="width: 100%" @change="onLineMethodChange(idx)">
              <a-select-option
                v-for="m in encaissementSplitLineMethods"
                :key="m"
                :value="m"
                >{{ $t(`decaissement.paymentMethod.${m}`) }}</a-select-option
              >
            </a-select>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12">
          <div
            class="mb-2"
            :class="{
              'payment-field--insufficient': lineAmountExceedsAvailable(line),
            }"
          >
            <label class="filter-label">{{ $t("recette.montant") }}</label>
            <a-input-number v-model="line.amount" :min="0" :precision="3" style="width: 100%" />
          </div>
        </a-col>
      </a-row>
      <template v-if="line.method === 'cash'">
        <div class="mb-2">
          <label class="filter-label">{{ $t("decaissement.fundSource") }}</label>
          <a-select v-model="line.fundDestination" style="width: 100%" @change="onLineFundDestinationChange(idx)">
            <a-select-option value="myCaisse" :disabled="!myCaisseId">{{ $t("decaissement.myCaisse") }}</a-select-option>
            <a-select-option value="otherCaisse" :disabled="!otherCaissesDeduped.length">{{
              $t("decaissement.otherCaisse")
            }}</a-select-option>
          </a-select>
        </div>
        <div
          v-if="line.fundDestination === 'myCaisse'"
          class="mb-2"
          :class="{
            'payment-field--insufficient': line.caisseId && lineCashAmountExceedsCaisse(line),
          }"
        >
          <label class="filter-label">{{ $t("decaissement.cashName") }}</label>
          <!-- Enabled as soon as the user owns more than one caisse. -->
          <a-select v-model="line.caisseId" style="width: 100%" :disabled="myCaisses.length <= 1">
            <a-select-option v-for="c in myCaisses" :key="c._id" :value="c._id">{{
              c.name
            }} — {{ cashAvailabilityForCaisse(c, line.method) }}</a-select-option>
          </a-select>
        </div>
        <div
          v-if="line.fundDestination === 'otherCaisse'"
          class="mb-2"
          :class="{
            'payment-field--insufficient': line.caisseId && lineCashAmountExceedsCaisse(line),
          }"
        >
          <label class="filter-label">{{ $t("decaissement.cashName") }}</label>
          <a-select v-model="line.caisseId" style="width: 100%" :placeholder="$t('requis.required')">
            <a-select-option v-for="c in otherCaissesDeduped" :key="c._id" :value="c._id"
              >{{ c.name }} — {{ cashAvailabilityForCaisse(c, line.method) }}</a-select-option
            >
          </a-select>
        </div>
        <p v-if="line.caisseId" class="text-muted small mb-0 mt-1">
          {{ $t("decaissement.availableForThisPayment") }}:
          <strong>{{ cashAvailabilityFormatted(line) }}</strong>
        </p>
        <p v-if="line.caisseId && lineCashAmountExceedsCaisse(line)" class="small mb-0 mt-1 text-danger">
          {{ $t("decaissement.caisseBalanceExceedsAvailWarning") }}
        </p>
      </template>
      <div
        v-else
        class="mb-2"
        :class="{
          'payment-field--insufficient': line.caisseBankId && lineBankAmountExceedsCaisse(line),
        }"
      >
        <label class="filter-label">{{ $t("decaissement.caisseBank") }}</label>
        <a-select v-model="line.caisseBankId" style="width: 100%" :placeholder="$t('requis.required')">
          <a-select-option v-for="b in caisseBanksList" :key="b._id" :value="b._id"
            >{{ b.name }} — {{ bankAvailabilityForCaisse(b, line.method) }}</a-select-option
          >
        </a-select>
        <p v-if="line.caisseBankId" class="text-muted small mb-0 mt-1">
          {{ $t("decaissement.availableForThisPayment") }}:
          <strong>{{ bankAvailabilityFormatted(line) }}</strong>
        </p>
        <p v-if="line.caisseBankId && lineBankAmountExceedsCaisse(line)" class="small mb-0 mt-1 text-danger">
          {{ $t("decaissement.caisseBalanceExceedsAvailWarning") }}
        </p>
      </div>

      <pay-cheque-section
        v-if="line.method === 'cheque'"
        :rows="line.chequeRows"
        :total-required="Number(line.amount) || 0"
        @update="(i, f, v) => updateLineDetailRow(idx, 'chequeRows', i, f, v)"
        @add="addLineDetailRow(idx, 'chequeRows')"
        @remove="removeLineDetailRow(idx, 'chequeRows', $event)"
      />
      <pay-traite-section
        v-if="line.method === 'traite'"
        :rows="line.traiteRows"
        :total-required="Number(line.amount) || 0"
        @update="(i, f, v) => updateLineDetailRow(idx, 'traiteRows', i, f, v)"
        @add="addLineDetailRow(idx, 'traiteRows')"
        @remove="removeLineDetailRow(idx, 'traiteRows', $event)"
      />
      <pay-virement-section
        v-if="line.method === 'virement'"
        :rows="line.virementRows"
        :total-required="Number(line.amount) || 0"
        @update="(i, f, v) => updateLineDetailRow(idx, 'virementRows', i, f, v)"
        @add="addLineDetailRow(idx, 'virementRows')"
        @remove="removeLineDetailRow(idx, 'virementRows', $event)"
      />
    </div>

    <a-button type="dashed" block icon="plus" class="mb-3" @click="addPaymentLine">
      {{ $t("encaissementValidation.addPaymentLine") }}
    </a-button>

    <div class="sum-bar mb-3" :class="{ match: splitsSumOk, mismatch: !splitsSumOk }">
      <span
        >{{ $t("encaissementValidation.splitsTotal") }}: {{ splitsSum.toFixed(3) }} /
        {{ Number(totalAmount || 0).toFixed(3) }}</span
      >
      <a-tag :color="splitsSumOk ? 'green' : 'red'">{{
        splitsSumOk ? "OK" : $t("encaissementValidation.splitsSumMismatch")
      }}</a-tag>
    </div>

    <div>
      <label class="filter-label">{{ $t("decaissement.receiptNumber") }}</label>
      <a-input v-model="receiptNumber" />
    </div>
  </div>
</template>

<script>
import {
  findCashCaisseInLookups,
  findBankCaisseInLookups,
  availableDecaissementForMethod,
  cashLineAmountExceedsAvailability,
  bankLineAmountExceedsAvailability,
  lineAmountExceedsPaymentSource,
} from "@/utilities/depense/caisseDecaissementAvailability";
import "../../components/paymentCaisseInsufficient.css";
import PayChequeSection from "../../components/paymentDetails/PayChequeSection.vue";
import PayTraiteSection from "../../components/paymentDetails/PayTraiteSection.vue";
import PayVirementSection from "../../components/paymentDetails/PayVirementSection.vue";
import { ENCAISSEMENT_SPLIT_LINE_METHODS } from "@/utilities/depense/encaissementFundSplitMethods";

export default {
  name: "DebtInitialReceiptStep",
  components: { PayChequeSection, PayTraiteSection, PayVirementSection },
  props: {
    totalAmount: { type: Number, default: 0 },
    lookups: {
      type: Object,
      default: () => ({ myCaisses: [], otherCaisses: [], caisseBanks: [] }),
    },
  },
  data() {
    return {
      paymentLines: [],
      receiptNumber: "",
    };
  },
  computed: {
    /** A user can own several caisses, all usable whatever the active school year. */
    myCaisses() {
      return this.lookups.myCaisses || [];
    },
    myCaisseId() {
      return this.myCaisses.length ? this.myCaisses[0]._id : undefined;
    },
    otherCaissesDeduped() {
      const list = this.lookups.otherCaisses || [];
      const mine = new Set(this.myCaisses.map((c) => String(c._id)));
      if (!mine.size) return list;
      return list.filter((c) => !mine.has(String(c._id)));
    },
    splitsSum() {
      return this.paymentLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
    },
    splitsSumOk() {
      return Math.abs(this.splitsSum - Number(this.totalAmount || 0)) < 0.01;
    },
    encaissementSplitLineMethods() {
      return ENCAISSEMENT_SPLIT_LINE_METHODS;
    },
    caisseBanksList() {
      return this.lookups.caisseBanks || [];
    },
  },
  watch: {
    totalAmount: {
      immediate: true,
      handler() {
        this.resetLines();
      },
    },
  },
  methods: {
    cashAvailabilityForCaisse(caisse, method) {
      const v = availableDecaissementForMethod(caisse, method);
      return v == null ? "—" : v.toFixed(3);
    },
    cashAvailabilityFormatted(line) {
      const c = findCashCaisseInLookups(this.lookups, line.caisseId);
      const v = availableDecaissementForMethod(c, line.method);
      return v == null ? "—" : v.toFixed(3);
    },
    lineCashAmountExceedsCaisse(line) {
      return cashLineAmountExceedsAvailability(this.lookups, line);
    },
    bankAvailabilityForCaisse(bank, method) {
      const v = availableDecaissementForMethod(bank, method);
      return v == null ? "—" : v.toFixed(3);
    },
    bankAvailabilityFormatted(line) {
      const b = findBankCaisseInLookups(this.lookups, line.caisseBankId);
      const v = availableDecaissementForMethod(b, line.method);
      return v == null ? "—" : v.toFixed(3);
    },
    lineBankAmountExceedsCaisse(line) {
      return bankLineAmountExceedsAvailability(this.lookups, line);
    },
    lineAmountExceedsAvailable(line) {
      return lineAmountExceedsPaymentSource(this.lookups, line);
    },
    emptyChequeRow() {
      return { num: null, dateEchance: null, bank: undefined, titulaire: null, montant: 0 };
    },
    emptyTraiteRow() {
      return { num: null, dateEchance: null, bank: undefined, rib: null, montant: 0 };
    },
    emptyVirementRow() {
      return { num: null, titulaire: null, montant: 0 };
    },
    defaultCashFundFields() {
      if (this.myCaisseId) {
        return { fundDestination: "myCaisse", caisseId: this.myCaisseId };
      }
      const first = this.otherCaissesDeduped[0];
      if (first) return { fundDestination: "otherCaisse", caisseId: first._id };
      return { fundDestination: "myCaisse", caisseId: undefined };
    },
    createPaymentLine() {
      const cash = this.defaultCashFundFields();
      return {
        uid: `pl_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        method: "cash",
        amount: null,
        fundDestination: cash.fundDestination,
        caisseId: cash.caisseId,
        caisseBankId: undefined,
        chequeRows: [this.emptyChequeRow()],
        traiteRows: [this.emptyTraiteRow()],
        virementRows: [this.emptyVirementRow()],
      };
    },
    resetLines() {
      this.receiptNumber = "";
      const line = this.createPaymentLine();
      line.amount = this.totalAmount;
      this.paymentLines = [line];
    },
    addPaymentLine() {
      const line = this.createPaymentLine();
      line.amount = 0;
      this.paymentLines = [...this.paymentLines, line];
    },
    removePaymentLine(idx) {
      if (this.paymentLines.length <= 1) return;
      this.paymentLines = this.paymentLines.filter((_, i) => i !== idx);
    },
    onLineMethodChange(idx) {
      const line = { ...this.paymentLines[idx] };
      line.chequeRows = [this.emptyChequeRow()];
      line.traiteRows = [this.emptyTraiteRow()];
      line.virementRows = [this.emptyVirementRow()];
      if (line.method === "cash") {
        const cash = this.defaultCashFundFields();
        line.fundDestination = cash.fundDestination;
        line.caisseId = cash.caisseId;
        line.caisseBankId = undefined;
      } else {
        line.fundDestination = "caisseBank";
        line.caisseId = undefined;
        line.caisseBankId = undefined;
        if (this.caisseBanksList.length) {
          line.caisseBankId = this.caisseBanksList[0]._id;
        }
      }
      this.$set(this.paymentLines, idx, line);
    },
    onLineFundDestinationChange(idx) {
      const line = { ...this.paymentLines[idx] };
      if (line.method !== "cash") return;
      if (line.fundDestination === "myCaisse") {
        line.caisseId = this.myCaisseId;
        line.caisseBankId = undefined;
      } else if (line.fundDestination === "otherCaisse") {
        line.caisseBankId = undefined;
        const first = this.otherCaissesDeduped[0];
        line.caisseId = first ? first._id : undefined;
      }
      this.$set(this.paymentLines, idx, line);
    },
    updateLineDetailRow(lineIdx, arrKey, rowIdx, field, val) {
      const line = { ...this.paymentLines[lineIdx] };
      const arr = [...line[arrKey]];
      arr[rowIdx] = { ...arr[rowIdx], [field]: val };
      line[arrKey] = arr;
      this.$set(this.paymentLines, lineIdx, line);
    },
    addLineDetailRow(lineIdx, arrKey) {
      const templates = {
        chequeRows: this.emptyChequeRow(),
        traiteRows: this.emptyTraiteRow(),
        virementRows: this.emptyVirementRow(),
      };
      const line = { ...this.paymentLines[lineIdx] };
      line[arrKey] = [...line[arrKey], { ...templates[arrKey] }];
      this.$set(this.paymentLines, lineIdx, line);
    },
    removeLineDetailRow(lineIdx, arrKey, rowIdx) {
      const line = { ...this.paymentLines[lineIdx] };
      line[arrKey] = line[arrKey].filter((_, i) => i !== rowIdx);
      this.$set(this.paymentLines, lineIdx, line);
    },
    detailSumForLine(line) {
      if (line.method === "cheque") return (line.chequeRows || []).reduce((s, r) => s + (Number(r.montant) || 0), 0);
      if (line.method === "traite") return (line.traiteRows || []).reduce((s, r) => s + (Number(r.montant) || 0), 0);
      if (line.method === "virement") return (line.virementRows || []).reduce((s, r) => s + (Number(r.montant) || 0), 0);
      return 0;
    },
    /**
     * @returns {{ splits: Array, receiptNumber: string } | null}
     */
    buildPayload() {
      if (!this.splitsSumOk) {
        this.$message.error(this.$t("encaissementValidation.splitsSumMismatch"));
        return null;
      }
      for (let i = 0; i < this.paymentLines.length; i += 1) {
        const line = this.paymentLines[i];
        const amt = Number(line.amount);
        if (!amt || amt <= 0) {
          this.$message.error(this.$t("encaissementValidation.lineAmountRequired"));
          return null;
        }
        if (line.fundDestination === "caisseBank" && !line.caisseBankId) {
          this.$message.error(this.$t("requis.required"));
          return null;
        }
        if (line.fundDestination === "caisseBank" && line.method === "cash") {
          this.$message.error(this.$t("errorCode.ENCAISSEMENT_BANK_INCOMPATIBLE_METHOD"));
          return null;
        }
        if (line.method !== "cash" && line.fundDestination !== "caisseBank") {
          this.$message.error(this.$t("errorCode.ENCAISSEMENT_NON_CASH_REQUIRES_BANK"));
          return null;
        }
        if (line.fundDestination !== "caisseBank" && !line.caisseId) {
          this.$message.error(this.$t("requis.required"));
          return null;
        }
        if (["cheque", "traite", "virement"].includes(line.method)) {
          const ds = this.detailSumForLine(line);
          if (Math.abs(ds - amt) > 0.01) {
            this.$message.error(this.$t("decaissement.methodDetailsSumError"));
            return null;
          }
        }
      }

      const splits = this.paymentLines.map((line) => {
        const base = {
          method: line.method,
          amount: Number(line.amount),
          fundDestination: line.fundDestination,
          caisseId: line.fundDestination !== "caisseBank" ? line.caisseId : undefined,
          caisseBankId: line.fundDestination === "caisseBank" ? line.caisseBankId : undefined,
        };
        if (line.method === "cheque") base.methodDetails = { cheques: line.chequeRows };
        else if (line.method === "traite") base.methodDetails = { traites: line.traiteRows };
        else if (line.method === "virement") base.methodDetails = { virements: line.virementRows };
        else base.methodDetails = {};
        return base;
      });

      return {
        splits,
        receiptNumber: this.receiptNumber || "",
      };
    },
  },
};
</script>

<style scoped>
.payment-split-card {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
}
.sum-bar {
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.sum-bar.match {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}
.sum-bar.mismatch {
  background: #fff2f0;
  border: 1px solid #ffccc7;
}
.filter-label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
}
</style>
